// src/satellites/SatelliteDetails.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import './SatelliteDetails.css';

export default function SatelliteDetails() {
  const navigate = useNavigate();
  const { id } = useParams();

  const API_ORIGIN = process.env.REACT_APP_API_URL;
  const API_BASE = API_ORIGIN ? `${API_ORIGIN}/api` : '/api';
  const token = localStorage.getItem('token');
  const myUsername = localStorage.getItem('username') || '';

  const [sat, setSat] = useState(null);
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  // поля (можно оставить пустыми — отправим, только если заполнены)
  const [lastName, setLastName] = useState('');
  const [tg, setTg] = useState('');

  const isRegistered = useMemo(() => {
    if (!myUsername) return false;
    return players.some(p => String(p.username || '').toLowerCase() === myUsername.toLowerCase());
  }, [players, myUsername]);

  const load = async () => {
    setLoading(true);
    setErr('');
    try {
      // 1) подтянем инфо о сателлите (чтобы показать title)
      const listRes = await axios.get(`${API_BASE}/satellites`, {
        params: { status: 'all' },
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const all = Array.isArray(listRes.data?.items) ? listRes.data.items : [];
      const found = all.find(x => String(x.id) === String(id));
      setSat(found || null);

      // 2) игроки
      const plRes = await axios.get(`${API_BASE}/satellites/${id}/players`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setPlayers(Array.isArray(plRes.data?.players) ? plRes.data.players : []);
    } catch (e) {
      console.error('[SatelliteDetails] load failed', e);
      setErr('Не удалось загрузить страницу сателлита');
      setPlayers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const onOpenTg = (p) => {
    const link = p?.telegramLink;
    if (!link) return;
    window.open(link, '_blank', 'noopener,noreferrer');
  };

  const register = async () => {
    setBusy(true);
    setErr('');
    try {
      const body = {};
      if (String(lastName || '').trim()) body.last_name = String(lastName).trim();
      if (String(tg || '').trim()) body.telegram_username = String(tg).trim();

      await axios.post(`${API_BASE}/satellites/${id}/register`, body, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      // обновляем список игроков
      const plRes = await axios.get(`${API_BASE}/satellites/${id}/players`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setPlayers(Array.isArray(plRes.data?.players) ? plRes.data.players : []);
    } catch (e) {
      console.error('[SatelliteDetails] register failed', e);
      const msg = e?.response?.data?.error || 'Не удалось зарегистрироваться';
      setErr(msg);
    } finally {
      setBusy(false);
    }
  };

  const unregister = async () => {
    setBusy(true);
    setErr('');
    try {
      await axios.post(`${API_BASE}/satellites/${id}/unregister`, {}, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      const plRes = await axios.get(`${API_BASE}/satellites/${id}/players`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setPlayers(Array.isArray(plRes.data?.players) ? plRes.data.players : []);
    } catch (e) {
      console.error('[SatelliteDetails] unregister failed', e);
      const msg = e?.response?.data?.error || 'Не удалось отменить регистрацию';
      setErr(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="satd-page">
      <div className="satd-top">
        <button className="satd-back" type="button" onClick={() => navigate('/satellites')}>
          ← Назад
        </button>

        <div className="satd-head">
          <div className="satd-title">
            {sat?.title || `Сателлит #${id}`}
          </div>
          <div className="satd-sub">
            статус: <b>{sat?.status || '—'}</b> · лимит: <b>{sat?.max_players ?? '—'}</b>
          </div>
        </div>
      </div>

      {err ? <div className="satd-error">{err}</div> : null}

      {loading ? (
        <div className="satd-loading">Загрузка…</div>
      ) : (
        <>
          <div className="satd-actions">
            {!isRegistered ? (
              <div className="satd-regbox">
                <div className="satd-regtitle">Регистрация</div>

                <div className="satd-inputs">
                  <label className="satd-field">
                    <span>Фамилия (если попросит)</span>
                    <input
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      placeholder="Напр.: Иванов"
                    />
                  </label>

                  <label className="satd-field">
                    <span>Telegram username (если попросит)</span>
                    <input
                      value={tg}
                      onChange={(e) => setTg(e.target.value)}
                      placeholder="Напр.: myname или @myname"
                    />
                  </label>
                </div>

                <button
                  className="satd-btn satd-btn--primary"
                  type="button"
                  disabled={busy}
                  onClick={register}
                >
                  {busy ? 'Регистрируем…' : 'Зарегистрироваться'}
                </button>
              </div>
            ) : (
              <button
                className="satd-btn satd-btn--danger"
                type="button"
                disabled={busy}
                onClick={unregister}
              >
                {busy ? 'Отмена…' : 'Отменить регистрацию'}
              </button>
            )}
          </div>

          <div className="satd-tablewrap">
            <div className="satd-tabletitle">
              Участники: <b>{players.length}</b>
            </div>

            {/* Desktop таблица */}
            <div className="satd-table satd-table--desktop">
              <div className="satd-tr satd-tr--head">
                <div>#</div>
                <div>Ник</div>
                <div>Фамилия</div>
                <div>Telegram</div>
              </div>

              {players.map((p, idx) => (
                <div className="satd-tr" key={p.id || `${p.username}-${idx}`}>
                  <div>{idx + 1}</div>

                  <div>
                    <button
                      className="satd-link"
                      type="button"
                      onClick={() => onOpenTg(p)}
                      title={p.telegramLink ? 'Открыть Telegram' : 'Нет Telegram'}
                      disabled={!p.telegramLink}
                    >
                      {p.username || '—'}
                    </button>
                    {p.username && myUsername && p.username.toLowerCase() === myUsername.toLowerCase()
                      ? <span className="satd-me">вы</span>
                      : null}
                  </div>

                  <div>{p.last_name || '—'}</div>

                  <div>
                    {p.telegram_username ? (
                      <button
                        className="satd-link"
                        type="button"
                        onClick={() => onOpenTg(p)}
                      >
                        @{String(p.telegram_username).replace(/^@+/, '')}
                      </button>
                    ) : '—'}
                  </div>
                </div>
              ))}

              {!players.length ? <div className="satd-empty">Пока никто не зарегистрирован</div> : null}
            </div>

            {/* Mobile список */}
            <div className="satd-cards satd-cards--mobile">
              {players.map((p, idx) => (
                <div className="satd-pcard" key={p.id || `${p.username}-${idx}`}>
                  <div className="satd-pcard__top">
                    <div className="satd-pcard__num">{idx + 1}</div>
                    <button
                      className="satd-pcard__nick"
                      type="button"
                      onClick={() => onOpenTg(p)}
                      disabled={!p.telegramLink}
                    >
                      {p.username || '—'}
                    </button>
                    {p.username && myUsername && p.username.toLowerCase() === myUsername.toLowerCase()
                      ? <span className="satd-me">вы</span>
                      : null}
                  </div>

                  <div className="satd-pcard__row">
                    <span>Фамилия:</span>
                    <b>{p.last_name || '—'}</b>
                  </div>

                  <div className="satd-pcard__row">
                    <span>Telegram:</span>
                    {p.telegram_username ? (
                      <button className="satd-link" type="button" onClick={() => onOpenTg(p)}>
                        @{String(p.telegram_username).replace(/^@+/, '')}
                      </button>
                    ) : (
                      <b>—</b>
                    )}
                  </div>
                </div>
              ))}

              {!players.length ? <div className="satd-empty">Пока никто не зарегистрирован</div> : null}
            </div>

          </div>
        </>
      )}
    </div>
  );
}
